<script setup lang="ts">
import type { UIColor } from "@align/ui";
import { useChromeStore } from "~/composables/stores/chrome";

defineOptions({ inheritAttrs: false });

const props = defineProps({
  visible: {
    type: [Boolean, Object, String, Number],
    required: true,
  },

  color: {
    type: String as PropType<UIColor>,
    required: true,
  },

  /*
      Per default; component automatically toggles overlay effect on Chrome.
      This property disables it.
    */
  chromeAutoOverlayDisable: {
    type: Boolean,
    default: false,
  },

  /*
      ChromeNotification enables "click outside" mode, and per default; targets class: "chrome-overlay-foreground" as it's qeue to close.
      This property allows for aditional classes to trigger a "click outside", in case chromeAutoOverlayDisable is present.
    */
  clickOutsideMatchCustomClassName: {
    type: String,
    default: undefined,
  },

  /**
   * On by default for backwards compatibility. Settings this to false unmounts the component when it's not visible
   * Besides being better for overall page performance, it also lets you use multiple modals on the same page.
   * When it's on, each modal will still use layout in the portal layer parent component.
   */
  keepMounted: {
    type: Boolean,
    default: true,
  },
});

const id = useId();

const emit = defineEmits<{ (event: "click-outside"): void }>();

const chromeStore = useChromeStore();

const visible = computed(() => !!props.visible);

const setNotificationActive = (active: boolean) => {
  chromeStore.visibleModals = active ? Array.from(new Set([...chromeStore.visibleModals, id])) : chromeStore.visibleModals.filter((_id) => _id !== id);
};

const clickOutside = (event: MouseEvent) => {
  const target = event.target as Node;

  const isElement = (element: Node): element is HTMLElement => "classList" in element;

  if (props.visible) {
    if (
      isElement(target) &&
      (target.classList.contains("portal-floating-actionLayers") ||
        target.classList.contains("chrome-overlay-foreground") ||
        target.classList.contains("portal-floating-action-layer") ||
        target.classList.contains("container-portal-height-limit"))
    ) {
      emit("click-outside");
    } else {
      const f = (n: Node | null) => {
        if (n && n !== document) {
          if (
            isElement(n) &&
            (n.classList.contains("chrome-container-content-start-top") ||
              n.classList.contains("chrome-container-content-end-top") ||
              n.classList.contains("chrome-container-aside"))
          ) {
            emit("click-outside");
          } else {
            f(n.parentNode);
          }
        }
      };

      f(target);
    }
  }
};

onBeforeUnmount(() => {
  setNotificationActive(false);
});

watch(visible, setNotificationActive, { immediate: true });

const container = useTemplateRef("container");

useClickOutside(container, clickOutside);

onMounted(() => {
  chromeStore.register(id);
});

onUnmounted(() => {
  chromeStore.unregister(id);
});
</script>

<template>
  <portal :to="`chrome-notification-layer-${id}`">
    <div
      v-if="visible || keepMounted"
      ref="container"
      :class="[$style.container, visible && $style.visible]"
      :style="{ 'background-color': `var(${color})` }"
      v-bind="$attrs"
    >
      <div :class="$style.layout">
        <slot />
      </div>
    </div>
  </portal>
</template>

<style module>
.container {
  display: flex;
  flex-direction: column;
  opacity: 0;
  overflow-y: hidden;
  height: 0;
  transform: translateY(-1rem);
  transition-property: opacity, transform, overflow;
  transition-delay: var(--transition-duration-short);
  transition-duration: var(--transition-duration-shortest);
  transition-timing-function: ease-in-out;
  align-self: center;
  justify-self: flex-start;
  box-shadow: var(--box-shadow-large);
  /* outline: solid 1px rgba(0, 0, 0, 0.2);
  outline-offset: -1px;
  padding: 1px; */
  margin-left: 1rem;
  margin-right: 1rem;
  cursor: default;
}

.visible {
  opacity: 1;
  height: 100%;
  transform: translateY(0);
  margin-bottom: 1.25rem;
  background-color: cyan;
}

.layout {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
}
</style>
